import { cognitoIdpOrigin, type QaCognitoAuth, type QaEnvironmentConfig } from "@yagni/shared";
import { Input } from "../ui/index.js";
import { Row, RowLink } from "./environmentControls.js";

interface Props {
  config: QaEnvironmentConfig;
  credentialNames: string[];
  onChange(patch: Partial<QaEnvironmentConfig>): void;
  /** Opens another settings row: the ones these warnings name. */
  onOpenRow?: (section: string) => void;
}

export const EMPTY_COGNITO_AUTH: QaCognitoAuth = {
  region: "",
  userPoolId: "",
  clientId: "",
  usernameSecret: "",
  passwordSecret: "",
  storageOrigin: "",
};

/**
 * The AWS Cognito test identity. The pool, its app client and the origin that
 * receives the session are plain configuration; the password, the app-client
 * secret and the authenticator seed are only ever named here and read from
 * saved credentials at run time.
 */
export function EnvironmentCognitoAuth({
  config,
  credentialNames,
  onChange,
  onOpenRow,
}: Props) {
  const auth = config.cognitoAuth ?? EMPTY_COGNITO_AUTH;
  const set = (patch: Partial<QaCognitoAuth>) =>
    onChange({ cognitoAuth: { ...auth, ...patch } });
  const endpoint = cognitoIdpOrigin(auth.region);
  const origins = [
    ...config.allowedOrigins,
    ...config.services.map((service) => `http://127.0.0.1:${service.port}`),
  ];
  const text = (
    label: string,
    key: "region" | "userPoolId" | "clientId",
    placeholder: string,
  ) => (
    <label>
      {label}
      <Input
        value={auth[key]}
        placeholder={placeholder}
        onChange={(event) => set({ [key]: event.target.value })}
      />
    </label>
  );
  const credential = (
    label: string,
    key: "usernameSecret" | "passwordSecret" | "clientSecretName" | "totpSecretName",
    optional = false,
  ) => {
    const value = auth[key] ?? "";
    return (
      <>
        <label>
          {label}
          <select
            value={value}
            onChange={(event) =>
              set({ [key]: event.target.value || undefined })
            }
          >
            <option value="">
              {optional ? "Not used" : "Choose a saved credential"}
            </option>
            {value && !credentialNames.includes(value) && (
              <option value={value}>{value} (missing)</option>
            )}
            {credentialNames.map((name) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </label>
        {value && !credentialNames.includes(value) && (
          <p role="alert">
            {value} is not saved. Add it under{" "}
            <RowLink section="credentials" onOpenRow={onOpenRow}>
              Variables
            </RowLink>{" "}
            before testing.
          </p>
        )}
      </>
    );
  };
  return (
    <>
      <Row
        title="Cognito user pool"
        help="The pool Fletcher signs the dedicated test identity into. Name the same app client the frontend uses, allow its USER_PASSWORD_AUTH flow, and approve its endpoint as an application origin."
      >
        <span className="wrk-srow-controls qa-service">
          {text("AWS region", "region", "us-east-1")}
          {text("User pool id", "userPoolId", "us-east-1_ab12CD34")}
          {text("App client id", "clientId", "1h57kf5cpq17m0eml12eu4rvv1")}
          {endpoint && !config.allowedOrigins.includes(endpoint) && (
            <p role="alert">
              Approve {endpoint} under{" "}
              <RowLink section="network" onOpenRow={onOpenRow}>
                Network
              </RowLink>{" "}
              as an application origin, or the application&rsquo;s own
              session refresh is blocked during the run.
            </p>
          )}
          <label>
            Session origin
            <select
              value={auth.storageOrigin}
              onChange={(event) => set({ storageOrigin: event.target.value })}
            >
              <option value="">Choose an application origin</option>
              {auth.storageOrigin && !origins.includes(auth.storageOrigin) && (
                <option value={auth.storageOrigin}>
                  {auth.storageOrigin} (not approved)
                </option>
              )}
              {origins.map((origin) => (
                <option key={origin}>{origin}</option>
              ))}
            </select>
          </label>
          {auth.storageOrigin && !origins.includes(auth.storageOrigin) && (
            <p role="alert">
              {auth.storageOrigin} is not one of this environment&rsquo;s
              application origins. Approve it under{" "}
              <RowLink section="network" onOpenRow={onOpenRow}>
                Network
              </RowLink>{" "}
              or choose a running service.
            </p>
          )}
        </span>
      </Row>
      <Row
        title="Test identity"
        help="Saved credentials, read in the backend and never handed to the application's own commands. Only the tokens the pool issues reach the browser."
      >
        <span className="wrk-srow-controls qa-service">
          {credential("Username credential", "usernameSecret")}
          {credential("Password credential", "passwordSecret")}
          {credential("App client secret", "clientSecretName", true)}
          {credential("Authenticator seed", "totpSecretName", true)}
          <span className="qa-note">
            The session lasts as long as the pool&rsquo;s access and ID tokens:
            no refresh token is installed, so a run cannot outlive them.
          </span>
          <span className="qa-note">
            Leave the authenticator seed empty only if this identity has no
            multi-factor step. An identity on SMS codes cannot be signed in
            unattended.
          </span>
        </span>
      </Row>
    </>
  );
}
